"use client";

import { useState } from "react";

import { RemoveScroll } from "react-remove-scroll";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

import { NavTree } from "./nav-tree";
import { ScrollSpyDebugger } from "./scrollspy-debugger";
import type { SectionNode } from "./types";
import { useScrollThreshold } from "./use-scroll-threshold";
import { useScrollspy } from "./use-scrollspy";

type Props = {
  sections: SectionNode[];
  triggerHeight?: `${number}%`;
  className?: string;
};

export function ScrollSpy({
  sections,
  triggerHeight = "0%",
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const triggerHeightPercent = parseInt(triggerHeight.replace("%", ""));
  const offset = `-${triggerHeightPercent}% 0% -${100 - triggerHeightPercent}% 0%`;

  const { activeId } = useScrollspy(
    sections.flatMap((section) =>
      section.type === "link"
        ? [section.id]
        : [section.id, ...section.links.map((link) => link.id)],
    ),
    { offset },
  );

  const showScrollSpy = useScrollThreshold(100);

  return (
    <div className={cn(className, "lg:bg-surface-grayscale-subtle")}>
      <ScrollSpyDebugger rootMargin={offset} />

      {/* Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-[calc(var(--z-index-header)-2)] lg:hidden transition-opacity duration-300 ease-in-out pointer-events-none bg-[rgba(62,65,95,0.25)]",
          open ? "opacity-100 pointer-events-auto" : "opacity-0",
        )}
        onClick={() => setOpen(false)}
      />

      {/* Mobile */}
      <RemoveScroll
        enabled={open}
        className="lg:hidden"
      >
        <Accordion
          type="single"
          collapsible
          className={cn(
            "fixed top-(--header-height) inset-x-0 z-[calc(var(--z-index-header)-1)] -translate-y-[200%] transition-transform duration-300 ease-in-out shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.10)] bg-surface-grayscale-subtle",
            showScrollSpy && "translate-y-[unset]",
          )}
          value={open ? "open" : ""}
          onValueChange={(value) => setOpen(value === "open")}
        >
          <AccordionItem value="open">
            <AccordionTrigger className="h-(--scrollspy-height) grid-cols-1 items-center bg-primary px-6 rounded-none border-0 text-text-grayscale-negative">
              <span className="h4">In This Article</span>
            </AccordionTrigger>
            <AccordionContent className="bg-surface-grayscale-subtle p-0 pb-2">
              <div className="max-h-[340px] overflow-y-auto">
                <div className="pt-2">
                  <NavTree
                    sections={sections}
                    activeId={activeId}
                    onNavigate={() => setOpen(false)}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </RemoveScroll>

      {/* Desktop */}
      <div className="hidden lg:block border border-border-grayscale-default rounded-[12px] overflow-hidden">
        <div className="bg-surface-grayscale-lighter flex items-center pl-6 pr-3 py-2 border-b border-b-border-grayscale-default rounded-t-[11px]">
          <span className="h4 text-text-grayscale-body">In this article</span>
        </div>
        <NavTree
          sections={sections}
          activeId={activeId}
        />
      </div>
    </div>
  );
}
